import { orgMutations } from '@repo/db/mutations/organization'
import { userMutations } from '@repo/db/mutations/user'
import { Toaster } from '@repo/ui/components/Toaster.js'
import { TooltipProvider } from '@repo/ui/components/Tooltip'
import { data } from 'react-router'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData
} from 'react-router'
import type { ShouldRevalidateFunction } from 'react-router'
import { ErrorCard } from './components/ErrorCard'
import { useToaster } from './hooks/useToaster'
import { authenticator } from './services/auth.server'
import { getCookie } from './utils/cookie.server'
import { env } from './utils/env.server'
import { getToast } from './utils/toast.server'

import '@repo/tailwind/tailwind.css'
import type { Route } from './+types/root'

declare global {
  interface Window {
    ENV: Awaited<ReturnType<typeof loader>>['data']['ENV']
  }
}

export const meta = ({ data }: Route.MetaArgs) => {
  return [{ title: data?.ENV.APP_NAME }]
}

export const loader = async ({ request }: Route.LoaderArgs) => {
  const { cookie, toast } = await getToast(request)
  const activeOrg = await getCookie(request, 'activeOrg')
  const auth = await authenticator.getAuthSession(request)

  const [user, org, memberships] = await Promise.all([
    auth
      ? userMutations.getAuthenticatedUser(auth.userId, activeOrg?.orgId)
      : null,
    activeOrg ? orgMutations.getById(activeOrg.orgId) : null,
    auth ? userMutations.getMemberships(auth.userId) : null
  ])

  return data(
    {
      ENV: {
        APP_ENV: env.APP_ENV,
        API_URL: env.API_URL,
        APP_NAME: env.APP_NAME,
        WEB_APP_URL: env.WEB_APP_URL,
        SENTRY_DSN: env.SENTRY_DSN
      },
      toast,
      authenticatedUser: user,
      authenticatedOrg: org,
      memberships,
      activeOrg
    },
    { headers: { 'set-cookie': cookie } }
  )
}

// Always refresh root data so active org from cookies stays in sync across navigations/prefetches.
export const shouldRevalidate: ShouldRevalidateFunction = () => true

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root')

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        {data?.ENV.APP_ENV === 'dev' ? (
          <script
            crossOrigin="anonymous"
            src="//unpkg.com/react-scan/dist/auto.global.js"
          />
        ) : null}
        <Links />
        {data ? (
          <script
            // biome-ignore lint/security/noDangerouslySetInnerHtml: this is fine
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(data.ENV)}`
            }}
          />
        ) : null}
        <script
          src="https://unpkg.com/@integry/sdk/dist/umd/index.umd.js"
          defer
        />
      </head>
      <body className="font-sans antialiased text-sm text-primary">
        <TooltipProvider delayDuration={400}>{children}</TooltipProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export function ErrorBoundary() {
  const error = useRouteError()

  return (
    <div className="h-dvh flex flex-col items-center justify-center bg-secondary">
      <ErrorCard error={error} />
    </div>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { toast } = loaderData
  useToaster(toast)

  return (
    <>
      <Outlet />
      <Toaster />
    </>
  )
}
